// Conteneur DOM dédié aux portails Radix.
//
// Racine du crash "NotFoundError: Failed to execute 'removeChild' on 'Node'"
// (683 occurrences / 22 utilisateurs sur 7 jours) :
//
// @radix-ui/react-portal fait `createPortal(<div>, document.body)` quand aucun
// `container` n'est fourni. TOUS les portails de l'app — Dialog, Select,
// DropdownMenu, Popover, Sheet — deviennent donc des enfants directs de
// <body>, aux côtés du root React et de tout ce que les scripts tiers y
// injectent (widget de chat, bannières, extensions du navigateur).
//
// Quand un Dialog contenant des Select se ferme, React démonte plusieurs de ces
// portails dans le MÊME commit. Chaque suppression déplace les index des
// suivants ; si un acteur extérieur touche <body> entre-temps, React appelle
// `parent.removeChild(node)` sur un nœud qui n'est plus enfant de <body> et le
// DOM lève NotFoundError. React route cette erreur vers
// `captureCommitPhaseError` : le commit est abandonné et l'arbre démonté — d'où
// les modales qui se ferment toutes seules en perdant la saisie.
//
// Le correctif : donner aux portails un parent stable qui n'appartient qu'à
// eux. Les suppressions se font alors entre frères d'un conteneur dont rien
// d'autre ne modifie le contenu, hors de portée des scripts tiers.

import { guardPortalNode } from "./portalNodeGuard";

export const PORTAL_CONTAINER_ID = "edgely-portal-root";

let container: HTMLElement | null = null;

/**
 * Conteneur partagé pour tous les portails Radix.
 *
 * Retourne `null` côté serveur (pas de document). Recrée le nœud s'il a été
 * retiré du DOM, pour qu'un script tiers agressif ne puisse pas laisser
 * l'application sans conteneur.
 */
export function getPortalContainer(): HTMLElement | null {
  if (typeof document === "undefined") return null;

  // `isConnected` couvre le cas d'un conteneur détaché par un tiers.
  if (container && container.isConnected) return container;

  // Pas de `instanceof HTMLElement` : le global n'existe pas partout (SSR,
  // iframe, environnement de test) et un nœud venu d'un autre document y
  // échouerait à tort.
  const existing = document.getElementById(PORTAL_CONTAINER_ID);
  if (existing) {
    container = existing as HTMLElement;
    guardPortalNode(container);
    return container;
  }

  const el = document.createElement("div");
  el.id = PORTAL_CONTAINER_ID;
  // Purement structurel : aucun style, pour ne pas créer de contexte
  // d'empilement qui casserait le z-index des overlays.
  document.body.appendChild(el);
  container = el;
  // Le conteneur héberge des portails dont les nœuds sont retirés à la fois par
  // React et par les animations de sortie de Radix. Sans ce garde, la course
  // entre les deux lève `removeChild` en commit phase et React démonte la page
  // (modale perdue en pleine saisie). Voir portalNodeGuard.ts.
  guardPortalNode(container);
  return container;
}

/** Réinitialise le cache mémoire. Tests uniquement. */
export function resetPortalContainer(): void {
  container = null;
}
